import { Heading } from "../atoms/Heading";
import Image from "next/image";
import { Paragraph } from "../atoms/Paragraph";
import FeatureItem from "../molecules/FeatureItem";
import { Button } from "../atoms/Button";
import { resolveLegacyLink } from "@/app/utils/linkUtils";
import Link from "next/link";

export type Feature = {
  id: string | number;
  iconSrc: string;
  title: string;
};

export type AboutData = {
  primaryTitle: string;
  secondaryTitle?: string;
  description?: string;
  imageSrc: string;
  imageAlt?: string;
  features?: Feature[];
};

type Props = {
  data: AboutData;
  className?: string;
};

export default function AboutSectionHome({ data, className }: Props) {
  const { primaryTitle, secondaryTitle, description, imageSrc, imageAlt } =
    data;

  return (
    <section className={["py-14 md:py-8", className ?? ""].join(" ")}>
      {/* Bloc vert avec container */}
      <div className="bg-secondaryGreen py-8">
        <div className="lms-container px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* Colonne image */}
            <div className="order-1 lg:order-2">
              <div className="relative rounded-[28px] overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,.12)]">
                <Image
                  src={imageSrc}
                  alt={imageAlt ?? ""}
                  className="cover"
                  width={1152}
                  height={1089}
                />
              </div>
            </div>

            {/* Colonne texte */}
            <div className="order-2 lg:order-1 relative z-10 lg:-mr-24">
              <div className="pb-6">
                <Heading
                  as="h2"
                  className="text-4xl md:text-5xl font-bold text-text"
                >
                  {primaryTitle}
                </Heading>
              </div>

              <div className="bg-white rounded-[28px] shadow-xl p-6 md:p-10 lg:p-12">
                <Heading as="h3" className="mb-4 font-playfair text-primaryGreen">
                  {secondaryTitle}
                </Heading>
                {description && <Paragraph>{description}</Paragraph>}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bloc features */}
      <div className="bg-background py-4">
        <div className="lms-container px-6 flex flex-col items-center gap-8">
          {data.features && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.features.map((f, idx) => (
                <FeatureItem
                  className="text-gray-700"
                  key={`about-feature-${f?.id ?? f?.title ?? "x"}-${idx}`}
                  iconSrc={f.iconSrc}
                  title={f.title}
                />
              ))}
            </div>
          )}
          <Link href={resolveLegacyLink("/qui-sommes-nous", "main")}>
            <Button
              className="bg-muted border-1 border-muted-foreground rounded-lg p-6 text-gray-700 text-lg font-medium hover:text-white hover:border-0"
            >
              EN SAVOIR PLUS
            </Button>

          </Link>

        </div>
      </div>
    </section>

  );
}
